<script lang="ts">
export default {
  name: 'FilterBar',
};
</script>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, onUnmounted } from 'vue';
import { ChevronLeft, ChevronRight } from '@lucide/vue';
import { registerResizeItem } from '@/utilities/resize';

const props = withDefaults(
  defineProps<{
    /** When true, show left/right arrow buttons when content overflows. Default true for v-slide-group parity. */
    showArrows?: boolean;
    /** Extra classes for the root element. */
    class?: string;
  }>(),
  {
    showArrows: true,
  }
);

const scrollRef = ref<HTMLElement | null>(null);
const canScrollLeft = ref(false);
const canScrollRight = ref(false);
let resizeObserver: ResizeObserver | null = null;

function updateScrollState() {
  const el = scrollRef.value;
  if (!el) return;
  const { scrollLeft, scrollWidth, clientWidth } = el;
  canScrollLeft.value = scrollLeft > 0;
  canScrollRight.value = scrollLeft < scrollWidth - clientWidth - 1;
}

function scroll(direction: 'left' | 'right') {
  const el = scrollRef.value;
  if (!el) return;
  const step = el.clientWidth * 0.8;
  el.scrollBy({ left: direction === 'left' ? -step : step, behavior: 'smooth' });
}

const rootClasses = computed(() => {
  const classes = [
    'flex',
    'items-center',
    'gap-0',
    'px-4',
    'py-2',
  ];
  if (props.class) {
    classes.push(props.class);
  }
  return classes.join(' ');
});

onMounted(() => {
  nextTick(() => {
    if (!scrollRef.value) return;
    scrollRef.value.addEventListener('scroll', updateScrollState);
    resizeObserver = new ResizeObserver(() => {
      updateScrollState();
    });
    resizeObserver.observe(scrollRef.value);
    updateScrollState();
  });
  registerResizeItem({ name: 'filter-bar', selector: 'filter-bar' });
});

onUnmounted(() => {
  scrollRef.value?.removeEventListener('scroll', updateScrollState);
  resizeObserver?.disconnect();
});
</script>

<template>
  <div id="filter-bar" :class="rootClasses">
    <button
      v-if="showArrows && canScrollLeft"
      type="button"
      class="btn btn-ghost btn-square btn-sm shrink-0"
      aria-label="Scroll left"
      @click="scroll('left')"
    >
      <ChevronLeft class="h-5 w-5" />
    </button>
    <div
      ref="scrollRef"
      class="flex flex-1 min-w-0 overflow-x-auto scroll-smooth gap-2 items-center"
      style="scrollbar-width: thin"
    >
      <slot name="filter-bar-left" />
      <slot />
      <slot name="filter-bar-right" />
    </div>
    <button
      v-if="showArrows && canScrollRight"
      type="button"
      class="btn btn-ghost btn-square btn-sm shrink-0"
      aria-label="Scroll right"
      @click="scroll('right')"
    >
      <ChevronRight class="h-5 w-5" />
    </button>
  </div>
</template>
